import { useState } from 'react';

import { ColorToken, formatCurrency, GenericTable } from '@backrs_ui/common-ui';

import {
  Box,
  SxProps,
  TableCell,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';

import { ProgramEarningsSort, ProgramEarningsSortKey } from './programStats';

export type ProgramEarningsRow = Readonly<{
  id: string;
  name: string;
  /** Null renders the missing-value marker. */
  awards: number | null;
  earnings: number;
}>;

type ProgramEarningsTableProps = Readonly<{
  id: string;
  /** Rows in display order; sorting is owned by the consumer. */
  rows: readonly ProgramEarningsRow[];
  sort: ProgramEarningsSort;
  onSortChange: (sort: ProgramEarningsSort) => void;
  labels: Readonly<{
    program: string;
    awards: string;
    earnings: string;
    missingValue: string;
    showMore: string;
    showLess: string;
    ariaLabel: string;
  }>;
  footnote?: string;
}>;

type StyleFn = (theme: Theme) => SystemStyleObject<Theme>;

const BAR_COLOR: ColorToken = 'Brand/500';
const DEFAULT_ROW_COUNT = 10;

const SORT_COLUMNS: readonly ProgramEarningsSortKey[] = ['program', 'awards', 'earnings'];

export const ProgramEarningsTable = ({
  id,
  rows,
  sort,
  onSortChange,
  labels,
  footnote,
}: ProgramEarningsTableProps) => {
  const [showAll, setShowAll] = useState(false);

  const maxEarnings = rows.reduce((max, row) => Math.max(max, row.earnings), 0);
  const visibleRows = showAll ? rows : rows.slice(0, DEFAULT_ROW_COUNT);
  const canToggle = rows.length > DEFAULT_ROW_COUNT;

  const toggleSort = (key: ProgramEarningsSortKey) => {
    if (sort.key === key) {
      onSortChange({ key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });

      return;
    }

    onSortChange({ key, direction: key === 'program' ? 'asc' : 'desc' });
  };

  const headers = SORT_COLUMNS.map((key) => ({
    sx: [headerCellSx, COLUMN_WIDTH_SX[key]],
    content: (
      <TableSortLabel
        id={`${id}-sort-${key}`}
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : key === 'program' ? 'asc' : 'desc'}
        onClick={() => toggleSort(key)}
        sx={[
          headerLabelSx,
          key !== 'program' && numericHeaderSx,
          key === 'earnings' && earningsHeaderSx,
        ]}
      >
        {labels[key]}
      </TableSortLabel>
    ),
  }));

  const footer = (footnote || canToggle) && (
    <Box sx={footerSx}>
      {footnote && (
        <Typography id={`${id}-footnote`} variant="text-sm-regular" sx={footnoteSx}>
          {footnote}
        </Typography>
      )}
      {canToggle && (
        <Typography
          id={`${id}-toggle`}
          variant="text-sm-semibold"
          role="button"
          tabIndex={0}
          onClick={() => setShowAll((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowAll((prev) => !prev);
            }
          }}
          sx={toggleSx}
        >
          {showAll ? labels.showLess : labels.showMore}
        </Typography>
      )}
    </Box>
  );

  return (
    <GenericTable
      id={id}
      ariaLabel={labels.ariaLabel}
      headers={headers}
      containerSx={containerSx}
      tableSx={tableSx}
      bodySx={tableBodySx}
      footer={footer}
    >
      {visibleRows.map((row) => (
        <TableRow key={row.id} id={`${id}-row-${row.id}`}>
          <TableCell sx={programCellSx}>
            <Typography variant="text-sm-medium" sx={primaryTextSx}>
              {row.name}
            </Typography>
          </TableCell>
          <TableCell sx={numericCellSx}>
            <Typography variant="text-sm-regular" sx={secondaryTextSx}>
              {row.awards == null ? labels.missingValue : row.awards.toLocaleString('en-US')}
            </Typography>
          </TableCell>
          <TableCell sx={earningsCellSx}>
            <Box sx={earningsContentSx}>
              <Box sx={barTrackSx}>
                <Box sx={barFillSx(BAR_COLOR, maxEarnings ? row.earnings / maxEarnings : 0)} />
              </Box>
              <Typography variant="text-sm-medium" sx={earningsValueSx}>
                {formatCurrency(row.earnings)}
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </GenericTable>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  overflowX: 'auto',
});

const headerCellSx: StyleFn = ({ backrs, typography, breakpoints }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
  backgroundColor: backrs.colors['Beige/50'],
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  paddingBlock: backrs.spacing['spacing-lg'],
  '&:first-of-type': { paddingLeft: backrs.spacing['spacing-3xl'] },
  '&:last-of-type': { paddingRight: backrs.spacing['spacing-3xl'] },
  [breakpoints.down('sm')]: {
    paddingInline: backrs.spacing['spacing-sm'],
    '&:first-of-type': { paddingLeft: backrs.spacing['spacing-xl'] },
    '&:last-of-type': { paddingRight: backrs.spacing['spacing-xl'] },
  },
});

const headerLabelSx = ({ backrs, typography, breakpoints }: Theme): SystemStyleObject<Theme> => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
  '&.Mui-active': { color: backrs.colors['Colors/Text/text-primary'] },
  '&:hover, &:focus-visible': { color: backrs.colors['Colors/Text/text-primary'] },
  '& .MuiTableSortLabel-icon, &.Mui-active .MuiTableSortLabel-icon': {
    color: backrs.colors['Colors/Text/text-primary'],
  },
  whiteSpace: 'nowrap',
  [breakpoints.down('sm')]: { ...typography['text-xs-semibold'] },
});

const tableBodySx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  '& td': {
    paddingBlock: backrs.spacing['spacing-xl'],
    borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  },
  '& tr:last-of-type td': { borderBottom: 0 },
  '& td:first-of-type': { paddingLeft: backrs.spacing['spacing-3xl'] },
  '& td:last-of-type': { paddingRight: backrs.spacing['spacing-3xl'] },
  [breakpoints.down('sm')]: {
    '& td': { paddingInline: backrs.spacing['spacing-sm'] },
    '& td:first-of-type': { paddingLeft: backrs.spacing['spacing-xl'] },
    '& td:last-of-type': { paddingRight: backrs.spacing['spacing-xl'] },
  },
});

const footerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  paddingBlock: backrs.spacing['spacing-xl'],
  paddingInline: backrs.spacing['spacing-3xl'],
  [breakpoints.down('sm')]: { paddingInline: backrs.spacing['spacing-xl'] },
});

const footnoteSx: SxProps<Theme> = ({ backrs }) => ({
  fontStyle: 'italic',
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const numericHeaderSx: SystemStyleObject<Theme> = {
  flexDirection: 'row-reverse',
  textAlign: 'right',
};

const earningsHeaderSx: StyleFn = ({ breakpoints }) => ({
  [breakpoints.down('sm')]: { whiteSpace: 'normal' },
});

const programCellSx: StyleFn = ({ breakpoints }) => ({
  width: '45%',
  [breakpoints.down('sm')]: { width: '32%' },
});

const tableSx: SxProps<Theme> = ({ breakpoints }) => ({
  [breakpoints.down('sm')]: { tableLayout: 'fixed' },
});

const numericCellSx: StyleFn = ({ breakpoints }) => ({
  width: '15%',
  textAlign: 'right',
  [breakpoints.down('sm')]: { width: '30%' },
});

const earningsCellSx: StyleFn = ({ breakpoints }) => ({
  width: '40%',
  [breakpoints.down('sm')]: { width: '38%' },
});

const COLUMN_WIDTH_SX: Readonly<Record<ProgramEarningsSortKey, StyleFn>> = {
  program: programCellSx,
  awards: numericCellSx,
  earnings: earningsCellSx,
};

const earningsContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const barTrackSx: SxProps<Theme> = ({ backrs }) => ({
  flex: '1 1 auto',
  height: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  overflow: 'hidden',
});

const barFillSx =
  (color: ColorToken, fraction: number): SxProps<Theme> =>
  ({ backrs }) => ({
    width: `${Math.round(fraction * 100)}%`,
    height: '100%',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: backrs.colors[color],
  });

const earningsValueSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  flex: '0 0 auto',
  minWidth: backrs.spacing['spacing-8xl'],
  [breakpoints.down('sm')]: { minWidth: 0 },
  textAlign: 'right',
  color: backrs.colors['Colors/Text/text-primary'],
});

const primaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const secondaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const toggleSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: 'auto',
  cursor: 'pointer',
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  '&:hover': {
    textDecoration: 'underline',
  },
});
